/// <reference types="vite/client" />

// CRITICAL RULE: Environment variables are the ONLY source of truth
// API URLs are set by Amplify during build based on branch:
// - main branch: VITE_API_BASE_URL_PROD (production)
// - other branches: VITE_API_BASE_URL_DEV (development)
//
// NO HARDCODED FALLBACKS - Code must read from env vars or error
// Exception: main branch can fallback to production URL as absolute last resort

// Production API Gateway URL (reference only, NOT used as fallback)
export const PRODUCTION_API_URL =
  "https://dq5kw8n238.execute-api.us-west-1.amazonaws.com/prod/api/v1";

// Production Auth API Gateway URL (auth endpoints now live on main API Gateway)
export const PRODUCTION_AUTH_API_URL =
  "https://dq5kw8n238.execute-api.us-west-1.amazonaws.com/prod";

/**
 * Helper function to ensure URL has /api/v1 suffix
 */
const ensureApiV1Suffix = (url: string): string => {
  if (url.endsWith("/api/v1")) {
    return url;
  }
  if (url.endsWith("/api/v1/")) {
    return url.slice(0, -1); // Remove trailing slash
  }
  return `${url}/api/v1`;
};

/**
 * Amplify zip-deploy uses branch name as the first DNS label (`<branch>.<appId>.amplifyapp.com`).
 * Do not hardcode a legacy Console app id; treat `main` / `prod` / `production` subdomains as
 * production-main only when the bundle is a production build (see `isMainBranch`).
 */
const isAmplifyProdMainSubdomain = (hostname: string): boolean => {
  if (!hostname.endsWith(".amplifyapp.com")) {
    return false;
  }
  const sub = hostname.split(".")[0]?.toLowerCase() ?? "";
  return sub === "main" || sub === "prod" || sub === "production";
};

/**
 * Detect if we're on the production **hosted** site (prod custom domain or prod Amplify branch).
 * SSR / no `window`: use explicit `VITE_ENVIRONMENT=production` + production MODE only.
 */
const isMainBranch = (): boolean => {
  const envTag = String(import.meta.env?.VITE_ENVIRONMENT ?? "")
    .trim()
    .toLowerCase();
  const envProductionBundle = envTag === "production";
  const modeProd = import.meta.env?.MODE === "production";

  try {
    if (typeof window !== "undefined" && window.location?.hostname) {
      const hostname = window.location.hostname;
      if (
        hostname === "kairos.cognixisai.com" ||
        hostname === "www.kairos.cognixisai.com"
      ) {
        return true;
      }
      if (
        envProductionBundle &&
        modeProd &&
        isAmplifyProdMainSubdomain(hostname)
      ) {
        return true;
      }
      return false;
    }
  } catch {
    /* extension / prerender */
  }

  return envProductionBundle && modeProd;
};

/**
 * Hosted on Amplify but not production-main (e.g. dev/staging/preview branches).
 */
const isNonMainAmplifyHostedHost = (): boolean => {
  try {
    if (typeof window === "undefined" || !window.location?.hostname) {
      return false;
    }
    const host = window.location.hostname;
    if (!host.endsWith(".amplifyapp.com")) {
      return false;
    }
    return !isMainBranch();
  } catch {
    return false;
  }
};

const cognitoMissingOnAmplifyBranchMessage = (
  name: "VITE_COGNITO_USER_POOL_ID" | "VITE_COGNITO_CLIENT_ID",
): string => {
  const prodKey =
    name === "VITE_COGNITO_USER_POOL_ID"
      ? "VITE_COGNITO_USER_POOL_ID_PROD"
      : "VITE_COGNITO_CLIENT_ID_PROD";
  const devKey =
    name === "VITE_COGNITO_USER_POOL_ID"
      ? "VITE_COGNITO_USER_POOL_ID_DEV"
      : "VITE_COGNITO_CLIENT_ID_DEV";
  return (
    `${name} was not embedded in this bundle (missing at build time). ` +
    `For AWS Amplify Hosting on a non-main branch, set ${devKey} (or _STAGING on develop) in the Amplify Console ` +
    "App settings → Environment variables, then trigger a new build. " +
    `For the main production branch, set ${prodKey}. ` +
    "If you publish from deploy-kairos.sh, ensure the frontend build step runs (do not set KAIROS_SKIP_FRONTEND_BUILD=1) so Cognito IDs are exported from CloudFormation."
  );
};

/**
 * Built-in prod Cognito fallbacks (non-production Vite MODE) are for local dev only.
 * Any real browser origin that is not localhost must embed pool/client at build time.
 */
const assertLocalhostOnlyForNonProdCognitoFallback = (
  viteName: "VITE_COGNITO_USER_POOL_ID" | "VITE_COGNITO_CLIENT_ID",
): void => {
  if (typeof window === "undefined" || !window.location?.hostname) {
    return;
  }
  const h = window.location.hostname;
  if (h === "localhost" || h === "127.0.0.1") {
    return;
  }
  throw new Error(cognitoMissingOnAmplifyBranchMessage(viteName));
};

/**
 * Get API base URL - Environment variables are the ONLY source of truth
 *
 * Priority:
 * 1. VITE_API_BASE_URL environment variable (set by Amplify)
 * 2. If main branch and no env var: fallback to production URL (absolute last resort)
 * 3. If local development and no env var: fallback to production URL so local testing hits prod
 * 4. If NOT main branch and NOT local dev and no env var: throw error (fail loudly)
 */
export const getApiBaseUrl = (): string => {
  // Priority 1: Environment variable (set by Amplify during build)
  const envUrl = import.meta.env?.VITE_API_BASE_URL;
  if (envUrl && envUrl.trim() !== "") {
    const finalUrl = ensureApiV1Suffix(envUrl.trim());
    return finalUrl;
  }

  // Priority 2: Main branch fallback to production (absolute last resort)
  if (isMainBranch()) {
    return PRODUCTION_API_URL;
  }

  // Priority 3: Local development — use prod so testing locally works against production
  if (isLocalDevelopment()) {
    return PRODUCTION_API_URL;
  }

  // Priority 4: NOT main branch and NOT local dev and no env var = ERROR
  throw new Error(
    "VITE_API_BASE_URL environment variable is required. " +
      "Please set it in Amplify console or .env file. " +
      "This ensures correct API endpoint for each environment. " +
      "Required variables: VITE_API_BASE_URL_PROD (for main branch) or VITE_API_BASE_URL_DEV (for other branches)",
  );
};

/**
 * Detect legacy/deleted auth API Gateway URLs
 */
const isLegacyAuthApiUrl = (url: string): boolean => {
  return url.includes("dvndt4n4w7.execute-api.us-west-1.amazonaws.com");
};

/**
 * Detect if we're in local development mode
 * Lazy-loaded to prevent initialization issues in extension context
 */
const isLocalDevelopment = (): boolean => {
  // Check Vite MODE (production builds will have MODE === 'production')
  const mode = import.meta.env?.MODE;
  return mode === "development";
};

/**
 * Non-production Vite bundles (development, staging, cfnhosted, etc.).
 * Amplify `develop` uses `vite build --mode staging`; Cognito env fallbacks must apply there too.
 */
const isNonProductionViteMode = (): boolean => {
  return import.meta.env?.MODE !== "production";
};

/**
 * Get Auth API base URL - Environment variables are the ONLY source of truth
 */
export const getAuthApiBaseUrl = (): string => {
  // Priority 1: Environment variable (set by Amplify during build)
  const envUrl = import.meta.env?.VITE_AUTH_API_BASE_URL;
  if (envUrl && envUrl.trim() !== "") {
    // Ensure URL has /api/v1 suffix for consistency
    const normalized = ensureApiV1Suffix(envUrl.trim());
    if (isLegacyAuthApiUrl(normalized)) {
      console.warn(
        "⚠️ VITE_AUTH_API_BASE_URL points to a legacy auth gateway. Using VITE_API_BASE_URL instead.",
      );
      return getApiBaseUrl();
    }
    return normalized;
  }

  // Priority 2: Main branch fallback to production (absolute last resort)
  if (isMainBranch()) {
    return ensureApiV1Suffix(PRODUCTION_AUTH_API_URL);
  }

  // Priority 3: Local development — use prod so testing locally works against production
  if (isLocalDevelopment()) {
    return ensureApiV1Suffix(PRODUCTION_AUTH_API_URL);
  }

  // Priority 4: NOT main branch and NOT local dev and no env var = ERROR
  throw new Error(
    "VITE_AUTH_API_BASE_URL environment variable is required. " +
      "Please set it in Amplify console or .env file. " +
      "Required variables: VITE_AUTH_API_BASE_URL_PROD (for main branch) " +
      "or VITE_AUTH_API_BASE_URL_DEV (for other branches)",
  );
};

// WebSocket API URL function removed - using localStorage-based cross-tab refresh instead

// Stripe Configuration - Test/Dev values (for local development fallback only)
const DEV_STRIPE_PUBLISHABLE_KEY =
  "pk_test_51SSPAM7AALEPi6TcHVTtu4q1gO11Ehhvdueo41niHmo5D72TVquniLv61o5BhZjcGio0fNewnnIAJoW66KPNwz8y00ex4mlrR8";
const DEV_STRIPE_PRICING_TABLE_ID = "prctbl_1SUfKK7AALEPi6TcxVsyHzMo";

/**
 * Get Stripe publishable key - Environment variables are the ONLY source of truth
 *
 * Priority:
 * 1. VITE_STRIPE_PUBLISHABLE_KEY environment variable (set by Amplify)
 * 2. If main branch and no env var: throw error (NO fallback to test key)
 * 3. If local development and no env var: allow test key fallback with warning
 * 4. If NOT main branch and NOT local dev and no env var: throw error (fail loudly)
 */
export const getStripePublishableKey = (): string => {
  // Priority 1: Environment variable (set by Amplify during build)
  const envKey = import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY;
  if (envKey && envKey.trim() !== "") {
    const key = envKey.trim();
    // Validate production key starts with pk_live_
    if (isMainBranch() && !key.startsWith("pk_live_")) {
      throw new Error(
        "Invalid Stripe publishable key for production. " +
          'Production key must start with "pk_live_". ' +
          "Current key: " +
          key.substring(0, 20) +
          "...",
      );
    }
    return key;
  }

  // Priority 2: Main branch - NO fallback, throw error
  if (isMainBranch()) {
    throw new Error(
      "VITE_STRIPE_PUBLISHABLE_KEY environment variable is required for production. " +
        "Please set VITE_STRIPE_PUBLISHABLE_KEY_PROD in Amplify console. " +
        'Production key must start with "pk_live_".',
    );
  }

  // Priority 3: Local development fallback to test key (for local dev only)
  // SECURITY: Only allow hardcoded fallback in true local development (localhost or 127.0.0.1)
  if (
    isLocalDevelopment() &&
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1")
  ) {
    console.warn(
      "⚠️  SECURITY: Using hardcoded test Stripe key. This should only happen in local development.",
    );
    return DEV_STRIPE_PUBLISHABLE_KEY; // Test key - ONLY for localhost
  }

  // Priority 4: NOT main branch and NOT local dev and no env var = ERROR
  throw new Error(
    "VITE_STRIPE_PUBLISHABLE_KEY environment variable is required. " +
      "Please set it in Amplify console or .env file. " +
      "Required variables: VITE_STRIPE_PUBLISHABLE_KEY_PROD (for main branch) " +
      "or VITE_STRIPE_PUBLISHABLE_KEY_DEV (for other branches)",
  );
};

/** Reject tutorial / copy-paste placeholders so Cognito never sees fake ids at runtime. */
const assertNotPlaceholderCognitoId = (
  value: string,
  viteName: string,
): void => {
  const v = value.trim().toLowerCase();
  if (v.includes("placeholder") || v === "changeme" || v === "your-client-id") {
    throw new Error(
      `${viteName}="${value}" is not a real Cognito id (looks like a placeholder). ` +
        "Rebuild with VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID from your environment's CloudFormation phase-1 outputs, " +
        "or run ./scripts/deploy-kairos.sh so the SPA build picks them up, then republish to Amplify.",
    );
  }
};

/**
 * Get Cognito User Pool ID - Environment variables are the ONLY source of truth
 *
 * Priority:
 * 1. VITE_COGNITO_USER_POOL_ID environment variable (set by Amplify)
 * 2. If main branch and no env var: throw error (NO fallback)
 * 3. If non-production Vite MODE (development, staging, cfnhosted, …) and no env var: built-in prod pool (localhost only)
 * 4. Otherwise: throw (fail loudly)
 */
export const getCognitoUserPoolId = (): string => {
  // Priority 1: Environment variable (set by Amplify during build)
  const envPoolId = import.meta.env?.VITE_COGNITO_USER_POOL_ID;
  if (envPoolId && envPoolId.trim() !== "") {
    const id = envPoolId.trim();
    assertNotPlaceholderCognitoId(id, "VITE_COGNITO_USER_POOL_ID");
    return id;
  }

  // Priority 2: Main branch - NO fallback, throw error
  if (isMainBranch()) {
    throw new Error(
      "VITE_COGNITO_USER_POOL_ID environment variable is required for production. " +
        "Please set VITE_COGNITO_USER_POOL_ID_PROD in Amplify console.",
    );
  }

  // Hosted dev/staging Amplify + production-mode bundle without Cognito env (common CI misconfig).
  if (isNonMainAmplifyHostedHost() && !isNonProductionViteMode()) {
    throw new Error(
      cognitoMissingOnAmplifyBranchMessage("VITE_COGNITO_USER_POOL_ID"),
    );
  }

  // Priority 3: Non-production Vite bundles (dev server, `build:dev`, `build:staging`, `build:cfnhosted`, …).
  if (isNonProductionViteMode()) {
    assertLocalhostOnlyForNonProdCognitoFallback("VITE_COGNITO_USER_POOL_ID");
    if (import.meta.env?.DEV) {
      console.warn(
        "[Cognito] VITE_COGNITO_USER_POOL_ID unset — using built-in prod User Pool ID (localhost only).",
      );
    }
    return "us-west-1_oe1uiLd7I"; // prod pool only (no dev)
  }

  // Priority 4: NOT main branch and NOT local dev and no env var = ERROR
  throw new Error(
    "VITE_COGNITO_USER_POOL_ID environment variable is required. " +
      "Set VITE_COGNITO_USER_POOL_ID in frontend/.env.local, or in Amplify: " +
      "VITE_COGNITO_USER_POOL_ID_PROD (main), VITE_COGNITO_USER_POOL_ID_STAGING (develop), " +
      "or VITE_COGNITO_USER_POOL_ID_DEV (other branches).",
  );
};

/**
 * Get Cognito Client ID - Environment variables are the ONLY source of truth
 *
 * Priority:
 * 1. VITE_COGNITO_CLIENT_ID environment variable (set by Amplify)
 * 2. If main branch and no env var: throw error (NO fallback)
 * 3. If non-production Vite MODE and no env var: built-in prod app client (localhost only)
 * 4. Otherwise: throw (fail loudly)
 */
export const getCognitoClientId = (): string => {
  // Priority 1: Environment variable (set by Amplify during build)
  const envClientId = import.meta.env?.VITE_COGNITO_CLIENT_ID;
  if (envClientId && envClientId.trim() !== "") {
    const id = envClientId.trim();
    assertNotPlaceholderCognitoId(id, "VITE_COGNITO_CLIENT_ID");
    return id;
  }

  // Priority 2: Main branch - NO fallback, throw error
  if (isMainBranch()) {
    throw new Error(
      "VITE_COGNITO_CLIENT_ID environment variable is required for production. " +
        "Please set VITE_COGNITO_CLIENT_ID_PROD in Amplify console.",
    );
  }

  if (isNonMainAmplifyHostedHost() && !isNonProductionViteMode()) {
    throw new Error(
      cognitoMissingOnAmplifyBranchMessage("VITE_COGNITO_CLIENT_ID"),
    );
  }

  // Priority 3: Same rules as getCognitoUserPoolId — non-production Vite MODE, localhost fallback only.
  if (isNonProductionViteMode()) {
    assertLocalhostOnlyForNonProdCognitoFallback("VITE_COGNITO_CLIENT_ID");
    if (import.meta.env?.DEV) {
      console.warn(
        "[Cognito] VITE_COGNITO_CLIENT_ID unset — using built-in prod app client ID (localhost only).",
      );
    }
    return "5jomiq94flcmh2cblkie8d2bfo"; // prod client only (no dev)
  }

  // Priority 4: NOT main branch and NOT local dev and no env var = ERROR
  throw new Error(
    "VITE_COGNITO_CLIENT_ID environment variable is required. " +
      "Set VITE_COGNITO_CLIENT_ID in frontend/.env.local, or in Amplify: " +
      "VITE_COGNITO_CLIENT_ID_PROD (main), VITE_COGNITO_CLIENT_ID_STAGING (develop), " +
      "or VITE_COGNITO_CLIENT_ID_DEV (other branches).",
  );
};

/**
 * Get Stripe pricing table ID - Environment variables are the ONLY source of truth
 *
 * Priority:
 * 1. VITE_STRIPE_PRICING_TABLE_ID environment variable (set by Amplify)
 * 2. If main branch and no env var: throw error (NO fallback to test ID)
 * 3. If local development and no env var: allow test ID fallback with warning
 * 4. If NOT main branch and NOT local dev and no env var: throw error (fail loudly)
 */
export const getStripePricingTableId = (): string => {
  // Priority 1: Environment variable (set by Amplify during build)
  const envId = import.meta.env?.VITE_STRIPE_PRICING_TABLE_ID;
  if (envId && envId.trim() !== "") {
    return envId.trim();
  }

  // Priority 2: Main branch - NO fallback, throw error
  if (isMainBranch()) {
    throw new Error(
      "VITE_STRIPE_PRICING_TABLE_ID environment variable is required for production. " +
        "Please set VITE_STRIPE_PRICING_TABLE_ID_PROD in Amplify console.",
    );
  }

  // Priority 3: Local development fallback to test ID (for local dev only)
  if (isLocalDevelopment()) {
    return DEV_STRIPE_PRICING_TABLE_ID;
  }

  // Priority 4: NOT main branch and NOT local dev and no env var = ERROR
  throw new Error(
    "VITE_STRIPE_PRICING_TABLE_ID environment variable is required. " +
      "Please set it in Amplify console or .env file. " +
      "Required variables: VITE_STRIPE_PRICING_TABLE_ID_PROD (for main branch) " +
      "or VITE_STRIPE_PRICING_TABLE_ID_DEV (for other branches)",
  );
};

// Specific endpoint URLs
export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: "/auth/login",
    REGISTER: "/auth/register",
    REFRESH: "/auth/refresh",
  },
  HEALTH: "/health",
  JOB_APPLICATIONS: "/job-applications",
  RESUMES: "/resumes",
  OPENAI: "/openai",
} as const;
